import { createFileRoute } from "@tanstack/react-router";
import React, { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { PageSkeleton } from "@/components/shared/Skeletons";
import { BookOpen, Clock, User, CheckCircle2, ArrowLeft, Play } from "lucide-react";
import { Link } from "@tanstack/react-router";
import PageHeader from "@/components/shared/PageHeader";
import { formatTZS } from "@/utils/formatters";
import { useEnrollCourse, useStudentCourses, useStudentEnrollments } from "@/hooks/useStudentData";
import { taskpartOrientationModules } from "@/data/taskpartProduct";
import PaymentModal from "@/components/shared/PaymentModal";
import { startSnippeCheckout, usePaymentReturn } from "@/lib/payments";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/store/authStore";
import SectionCard from "@/components/shared/SectionCard";
import EmptyState from "@/components/shared/EmptyState";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import apiClient from "@/lib/api-client";
import { useQuery } from "@tanstack/react-query";
import { unwrapApiArray } from "@/lib/api-response";

export const Route = createFileRoute("/student/courses/$id/")({ component: CourseDetail });

type CourseItem = {
  id: string | number;
  title: string;
  category: string;
  image: string;
  instructor: string;
  duration: string;
  enrollments: number;
  description: string;
  fee: number;
  type: string;
};

function CourseDetail() {
  const { id } = Route.useParams();
  const { data: courses = [], isLoading } = useStudentCourses();
  const { data: enrollments = [] } = useStudentEnrollments();
  const enrollCourse = useEnrollCourse();
  const queryClient = useQueryClient();

  // Back from Snippe checkout: the API enrolls the student once the payment
  // clears, so refresh what this page shows about enrollment.
  usePaymentReturn((status) => {
    if (status === "completed" || status === "verified") {
      queryClient.invalidateQueries({ queryKey: ["student-enrollments"] });
      queryClient.invalidateQueries({ queryKey: ["student-courses"] });
    }
  });
  const { user } = useAuthStore();
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const navigate = useNavigate();

  const course = courses.find((c: any) => String(c.id) === String(id) || (c.slug && String(c.slug) === String(id)));
  const isEnrolled = course ? enrollments.some((e: any) => String(e.course_id) === String(course.id)) : false;
  const isDemo = !!user?.isDemo;

  // Fetch lesson progress data to show real module completion status
  const { data: lessonProgress = [] } = useQuery({
    queryKey: ["course-lessons", id],
    queryFn: async () => {
      if (user?.isDemo) return [];
      try {
        const res = await apiClient.get(`/courses/${id}/lessons`);
        return unwrapApiArray<any>(res);
      } catch {
        return [];
      }
    },
    // We fetch lessons even if the student is not yet enrolled so we can display the 
    // actual dynamic syllabus generated from the remote site.
  });

  const handleEnrollClick = () => {
    if (!course) return;
    if (course.fee > 0) {
      setPaymentModalOpen(true);
    } else {
      enrollCourse.mutate(course.id);
    }
  };

  const handlePaymentConfirm = async () => {
    if (!course) return;
    if (isDemo) {
      setIsProcessingPayment(true);
      await new Promise((resolve) => setTimeout(resolve, 2000));
      setIsProcessingPayment(false);
      setPaymentModalOpen(false);
      enrollCourse.mutate(course.id);
      return;
    }
    // Leaves for Snippe checkout; enrollment happens once the payment clears.
    // Errors propagate so the payment modal can show them.
    await startSnippeCheckout({
      type: "course",
      referenceId: course.id,
      returnPath: `/student/courses/${course.id}`,
    });
  };

  if (isLoading) {
    return <PageSkeleton />;
  }

  if (!course) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <EmptyState
          className="w-full max-w-md"
          icon={<BookOpen />}
          title="Course not found"
          description="The course you're looking for doesn't exist."
          actionLabel="Back to Courses"
          onAction={() => navigate({ to: "/student/courses" })}
        />
      </div>
    );
  }

  // Build modules from real lesson progress data (API) or from course description
  const modules =
    course.type === "orientation"
      ? taskpartOrientationModules.map((module, index) => ({
          title: module,
          duration: index === 0 ? "20 min" : "25 min",
          completed: false,
        }))
      : lessonProgress.length > 0
        ? lessonProgress.map((lesson: any) => ({
            title: lesson.title || `Lesson ${lesson.order || 0}`,
            duration: lesson.duration || "15-20 min",
            completed: !!lesson.completed,
          }))
        : [
            { title: "Introduction & Overview", duration: "1h 30m", completed: false },
            { title: "Core Concepts", duration: "2h 30m", completed: false },
            { title: "Practical Exercises", duration: "3h", completed: false },
            { title: "Advanced Topics", duration: "2h", completed: false },
            { title: "Final Assessment", duration: "1h", completed: false },
          ];

  const metaRows = [
    { icon: <User />, label: "Instructor", value: course.instructor },
    { icon: <Clock />, label: "Duration", value: course.duration },
    { icon: <BookOpen />, label: "Enrolled", value: `${course.enrollments} students` },
  ];

  return (
    <div>
      <PageHeader
        title={course.title}
        subtitle={course.category}
        breadcrumbs={[
          { label: "Dashboard", path: "/student/dashboard" },
          { label: "Courses", path: "/student/courses" },
          { label: course.title },
        ]}
      />

      {course.type === "orientation" && (
        <Alert variant="success" className="mb-6">
          <CheckCircle2 />
          <AlertDescription>
            Complete this orientation before applying for verified work slots.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 md:grid-cols-12">
        {/* Main content */}
        <div className="min-w-0 space-y-6 md:col-span-8">
          <img
            src={course.image}
            alt={course.title}
            className="h-50 w-full rounded-md border border-border bg-muted object-cover text-[0px] md:h-80"
            loading="lazy"
          />

          <section className="rounded-md border border-border bg-card p-4 md:p-6">
            <Badge variant="secondary" className="mb-3">
              {course.category}
            </Badge>
            <h2 className="mb-5 text-display-sm font-normal text-foreground max-md:text-[22px] max-md:leading-7">
              {course.title}
            </h2>
            <dl className="mb-6 grid gap-4 sm:grid-cols-3">
              {metaRows.map((row, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <span className="text-muted-foreground [&_svg]:size-4" aria-hidden>
                    {row.icon}
                  </span>
                  <div>
                    <dt className="eyebrow">{row.label}</dt>
                    <dd className="text-sm font-medium text-foreground">{row.value}</dd>
                  </div>
                </div>
              ))}
            </dl>
            <p className="text-[15px] leading-7 text-body">{course.description}</p>
          </section>

          <SectionCard title="Course modules" noPadding>
            <ol className="divide-y divide-border">
              {modules.map((module, index) => (
                <li key={index} className="flex items-center gap-3 px-4 py-3.5 md:px-5">
                  <CheckCircle2
                    className={cn(
                      "size-5 shrink-0",
                      module.completed ? "text-success" : "text-hairline-strong",
                    )}
                    aria-label={module.completed ? "Completed" : "Not completed"}
                  />
                  <div className="min-w-0">
                    <p
                      className={cn(
                        "text-sm",
                        module.completed ? "font-medium text-foreground" : "text-body",
                      )}
                    >
                      {module.title}
                    </p>
                    <p className="text-caption text-muted-foreground">{module.duration}</p>
                  </div>
                </li>
              ))}
            </ol>
          </SectionCard>
        </div>

        {/* Sidebar */}
        <aside className="min-w-0 md:col-span-4">
          <div className="space-y-4 md:sticky md:top-6">
            <section className="rounded-md border border-border bg-card p-4 md:p-5">
              <p className="eyebrow mb-1">Course fee</p>
              <p className="mb-5 text-[32px] leading-none font-normal tracking-[-0.8px] text-foreground tabular-nums">
                {course.fee === 0 ? "Free" : formatTZS(course.fee)}
              </p>

              <div className="space-y-2">
                {isEnrolled ? (
                  <Button
                    size="lg"
                    className="w-full"
                    onClick={() => navigate({ to: "/student/courses/$id/learn", params: { id: id as string } })}
                  >
                    <Play />
                    Resume Learning
                  </Button>
                ) : (
                  <Button
                    size="lg"
                    className="w-full"
                    disabled={enrollCourse.isPending || isProcessingPayment}
                    onClick={handleEnrollClick}
                  >
                    {enrollCourse.isPending || isProcessingPayment
                      ? "Processing..."
                      : course.fee === 0
                        ? course.type === "orientation"
                          ? "Start Orientation"
                          : "Enroll for Free"
                        : `Enroll & Pay ${formatTZS(course.fee)}`}
                  </Button>
                )}

                <Button asChild variant="outline" className="w-full">
                  <Link to="/student/courses">
                    <ArrowLeft />
                    Back to Courses
                  </Link>
                </Button>
              </div>
            </section>

            <section className="rounded-md border border-border bg-card p-4 md:p-5">
              <h2 className="mb-2 text-sm font-medium text-foreground">What you'll learn</h2>
              <p className="text-sm leading-relaxed text-muted-foreground">
                This course covers practical skills aligned with industry standards. Complete
                all modules and assessments to receive your certificate of completion.
              </p>
            </section>
          </div>
        </aside>
      </div>

      <PaymentModal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        courseName={course.title}
        amount={course.fee}
        onSubmit={handlePaymentConfirm}
      />
    </div>
  );
}
